import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";
import { Loader2, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

// ---- Information tab: edit contact, location, hours, map (syncs to /visit + footer) ----
const DAYS_OF_WEEK = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

export default function InformationTab() {
  const { info: liveInfo, refresh: refreshContext } = useBusinessInfo();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Hydrate form from the context's last-known info
  useEffect(() => {
    if (!form) {
      setForm({
        ...liveInfo,
        hours: ensureSevenDays(liveInfo.hours),
      });
    }
  }, [liveInfo, form]);

  if (!form) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHour = (idx, field, value) => {
    setForm((f) => {
      const hours = (f.hours || []).map((h, i) => i === idx ? { ...h, [field]: value } : h);
      return { ...f, hours };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        phone: form.phone,
        whatsapp: form.whatsapp,
        email: form.email,
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        country: form.country,
        maps_embed_url: form.maps_embed_url,
        maps_directions_url: form.maps_directions_url,
        parking_info: form.parking_info,
        matatu_info: form.matatu_info,
        google_review_url: form.google_review_url,
        tutorial_video_url: form.tutorial_video_url,
        hours: form.hours,
      };
      await api.put("/admin/business/info", payload);
      await refreshContext();
      toast.success("Business info saved · synced to /visit and footer");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const directionsHint = form.maps_embed_url && !form.maps_embed_url.includes("output=embed")
    ? "We'll auto-append ?output=embed so the map iframe works."
    : null;

  return (
    <div className="space-y-6" data-testid="information-tab">
      <div className="bg-white border border-[#E5E5E5] p-5">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ Public-facing</div>
        <h2 className="font-display font-black text-2xl">Business information</h2>
        <p className="text-xs text-[#525252] mt-1 max-w-2xl">
          Everything here syncs to the public <span className="font-bold">/visit</span> page, the site footer, and order confirmations. Changes go live instantly.
        </p>
      </div>

      {/* Identity */}
      <Section title="Identity">
        <Grid>
          <TextField label="Business name" value={form.name} onChange={(v) => set("name", v)} testId="info-name" />
          <TextField label="Tagline" value={form.tagline} onChange={(v) => set("tagline", v)} testId="info-tagline" placeholder="Upload. Configure. Pay. Pick up." />
        </Grid>
      </Section>

      {/* Contact */}
      <Section title="Contact">
        <Grid>
          <TextField label="Phone (primary)" value={form.phone} onChange={(v) => set("phone", v)} testId="info-phone" placeholder="0733 737 993" />
          <TextField label="WhatsApp number" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} testId="info-whatsapp" placeholder="0733 737 993" />
          <TextField label="Email" value={form.email} onChange={(v) => set("email", v)} testId="info-email" placeholder="help@ctpkenya.com" />
        </Grid>
      </Section>

      {/* Location */}
      <Section title="Location">
        <Grid>
          <TextField label="Address line 1" value={form.address_line1} onChange={(v) => set("address_line1", v)} testId="info-address-1" placeholder="Tom Mboya Street, near Khoja Mosque" full />
          <TextField label="Address line 2 (optional)" value={form.address_line2} onChange={(v) => set("address_line2", v)} testId="info-address-2" placeholder="Ground floor — look for the green CTP banner" full />
          <TextField label="City / Area" value={form.city} onChange={(v) => set("city", v)} testId="info-city" placeholder="Nairobi CBD" />
          <TextField label="Country" value={form.country} onChange={(v) => set("country", v)} testId="info-country" placeholder="Kenya" />
        </Grid>
        <div className="mt-4 space-y-3">
          <TextField
            label="Google Maps embed URL"
            value={form.maps_embed_url}
            onChange={(v) => set("maps_embed_url", v)}
            testId="info-maps-embed"
            placeholder="https://www.google.com/maps?q=Tom+Mboya+Street+Nairobi"
            full
            help={directionsHint || "Open Google Maps → click your pin → Share → 'Embed a map' → copy URL"}
          />
          <TextField
            label="Google Maps directions URL"
            value={form.maps_directions_url}
            onChange={(v) => set("maps_directions_url", v)}
            testId="info-maps-dir"
            placeholder="https://www.google.com/maps/dir/?api=1&destination=..."
            full
            help="Used by the 'Get directions' button on /visit. Same Share menu → tab 'Send to phone' or copy the URL from the location."
          />
          {form.maps_embed_url && (
            <div className="aspect-[4/2] border border-[#E5E5E5] overflow-hidden bg-[#F5F5F5]" data-testid="info-map-preview">
              <iframe
                title="map preview"
                src={form.maps_embed_url.includes("output=embed") ? form.maps_embed_url : `${form.maps_embed_url}${form.maps_embed_url.includes("?") ? "&" : "?"}output=embed`}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>
      </Section>

      {/* Marketing content — public URLs used on Home, /how-it-works, /visit */}
      <Section title="Marketing content">
        <div className="space-y-3">
          <TextField
            label="Tutorial video URL (YouTube)"
            value={form.tutorial_video_url}
            onChange={(v) => set("tutorial_video_url", v)}
            testId="info-tutorial-video"
            placeholder="https://www.youtube.com/watch?v=xxxxxxxxxxx"
            full
            help="Powers the embedded video on the /how-it-works page. Any YouTube URL (watch, shorts, /embed/, or raw video ID) works — we normalise it. Leave empty to show a 'Coming soon' placeholder."
          />
          <TextField
            label="Google review URL (optional)"
            value={form.google_review_url}
            onChange={(v) => set("google_review_url", v)}
            testId="info-google-review"
            placeholder="https://g.page/r/xxxxxxxxxxxxxxx/review"
            full
            help="If set, we show a 'Leave a Google review' link on the /visit page. Get this from your Google Business Profile → Reviews → Get more reviews."
          />
        </div>
      </Section>

      {/* Hours */}
      <Section title="Opening hours">        <div className="space-y-2" data-testid="info-hours-editor">
          {(form.hours || []).map((h, idx) => (
            <div key={h.day} className="flex items-center gap-2 flex-wrap">
              <span className="w-24 text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">{h.day}</span>
              <label className="inline-flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={h.closed}
                  onChange={(e) => setHour(idx, "closed", e.target.checked)}
                  data-testid={`info-hour-closed-${h.day}`}
                />
                <span className="text-[#525252]">Closed</span>
              </label>
              <input
                type="time"
                value={h.open || ""}
                disabled={h.closed}
                onChange={(e) => setHour(idx, "open", e.target.value)}
                data-testid={`info-hour-open-${h.day}`}
                className="px-2 py-1 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono disabled:opacity-50"
              />
              <span className="text-[#525252]">–</span>
              <input
                type="time"
                value={h.close || ""}
                disabled={h.closed}
                onChange={(e) => setHour(idx, "close", e.target.value)}
                data-testid={`info-hour-close-${h.day}`}
                className="px-2 py-1 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono disabled:opacity-50"
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Pickup helpers */}
      <Section title="Pickup helpers (shown on /visit)">
        <Grid>
          <TextField label="Parking info" value={form.parking_info} onChange={(v) => set("parking_info", v)} testId="info-parking" placeholder="Paid roadside parking on Tom Mboya St..." full />
          <TextField label="Matatu / on-foot info" value={form.matatu_info} onChange={(v) => set("matatu_info", v)} testId="info-matatu" placeholder="Nearest matatu stage: Afya Centre (2 min walk)." full />
        </Grid>
      </Section>

      <StoragePurgeSection />

      <div className="sticky bottom-4 bg-white border-2 border-[#0A0A0A] p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div className="text-xs text-[#525252] uppercase tracking-[0.15em]">Changes apply instantly to /visit and the footer.</div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="info-save"
          className="inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save & sync
        </button>
      </div>
    </div>
  );
}

function ensureSevenDays(hours) {
  const existing = Array.isArray(hours) ? hours.reduce((m, h) => { m[h.day] = h; return m; }, {}) : {};
  return DAYS_OF_WEEK.map((d) => existing[d] || { day: d, open: "", close: "", closed: true });
}

function StoragePurgeSection() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/storage/purge-config");
      setCfg(data);
      setDraft({});
    } catch (e) {
      // Non-super admins can't read this — silently skip but keep a trace
      devLog.debug("settings.load skipped:", e?.response?.status);
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const cur = (key) => (key in draft ? draft[key] : cfg[key]);
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/storage/purge-config", draft);
      setCfg(data);
      setDraft({});
      toast.success("File retention saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm(`Purge files of ${cfg.purge_statuses.join(" + ")} orders older than ${cur("purge_after_days")} days?\n\nThis deletes the uploaded PDFs from storage. Order records and receipts are kept.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/storage/purge-now");
      toast.success(`Purged ${data.files_deleted} file${data.files_deleted === 1 ? "" : "s"} across ${data.orders_touched} order${data.orders_touched === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  return (
    <Section title="File retention & auto-purge">
      <p className="text-xs text-[#525252] mb-4 -mt-2">
        Delete uploaded source files for old completed orders to free storage. Order records, receipts and audit trail are <strong>always kept</strong> for accounting.
        A background job runs daily at 03:00 UTC when enabled.
      </p>
      <div className="grid md:grid-cols-3 gap-4" data-testid="purge-config">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Auto-purge</label>
          <button
            type="button"
            data-testid="purge-toggle"
            onClick={() => setDraft({ ...draft, purge_enabled: !cur("purge_enabled") })}
            className={`w-full px-3 py-2 border text-xs font-bold uppercase tracking-[0.15em] ${cur("purge_enabled") ? "border-[#059669] bg-[#059669]/10 text-[#059669]" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
          >
            {cur("purge_enabled") ? "Enabled" : "Disabled"}
          </button>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Delete files older than</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={7}
              value={cur("purge_after_days")}
              onChange={(e) => setDraft({ ...draft, purge_after_days: parseInt(e.target.value || "7") })}
              data-testid="purge-days"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
            />
            <span className="text-xs text-[#525252] uppercase tracking-[0.2em]">days</span>
          </div>
          <div className="text-[10px] text-[#525252] mt-1">Minimum 7 days.</div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Eligible statuses</label>
          <div className="flex flex-wrap gap-1.5" data-testid="purge-statuses">
            {["completed", "cancelled", "ready"].map((s) => {
              const list = cur("purge_statuses") || [];
              const on = list.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    const next = on ? list.filter((x) => x !== s) : [...list, s];
                    if (next.length === 0) { toast.error("Pick at least one status"); return; }
                    setDraft({ ...draft, purge_statuses: next });
                  }}
                  data-testid={`purge-status-${s}`}
                  className={`px-2.5 py-1.5 border text-[10px] font-bold uppercase tracking-[0.15em] ${on ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#E5E5E5] flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252]">
          {cfg.last_purge_at ? (
            <>Last run: <strong>{new Date(cfg.last_purge_at).toLocaleString()}</strong> · {cfg.last_purge_files_deleted} files · {cfg.last_purge_orders_touched} orders</>
          ) : (
            <>Never run yet.</>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runNow}
            disabled={purging || dirty}
            data-testid="purge-run-now"
            title={dirty ? "Save your changes first" : "Run the purge with current settings"}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold disabled:opacity-50"
          >
            {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Run purge now
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="purge-save"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-white hover:bg-[#047857] text-[10px] uppercase tracking-[0.2em] font-bold disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save retention
          </button>
        </div>
      </div>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-[#E5E5E5] p-5">
      <h3 className="font-display font-black text-lg mb-4 pb-2 border-b border-[#E5E5E5]">{title}</h3>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function TextField({ label, value, onChange, testId, placeholder, help, full }) {
  return (
    <div className={full ? "md:col-span-2" : undefined}>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">{label}</label>
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
      />
      {help && <div className="mt-1 text-[10px] text-[#525252]">{help}</div>}
    </div>
  );
}
